import React, { useState, useEffect, useRef } from "react";
import "./DocumentManagement.css";
import { useNavigate } from 'react-router-dom';
import DocumentHero from "./DocumentHero";
import DocumentWiseCode from "./DocumentWiseCode";
import HRCopilotFeatures from './HRCopilotFeatures';
import HRCopilotNewsSection from "./HRCopilotNewsSection";



const VideoPopup = ({ isOpen, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="document-video-popup-overlay" onClick={onClose}>
      <div className="document-video-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="document-video-popup-close" onClick={onClose}>
          ×
        </button>
        <div className="document-video-container">
          <video ref={videoRef} controls autoPlay className="document-demo-video">
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="document-video-popup-info">
          <h3>Document Management Platform</h3>
          <p>See how our document management system organizes, secures, and streamlines your document workflows</p>
        </div>
      </div>
    </div>
  );
};

const DocumentManagement = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const handleAccessRecruit = () => navigate('/access-recruit');

  useEffect(() => {
    setIsVisible(true);
    
    // Auto rotate features
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 4000);
    
    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const demoVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-woman-organizing-documents-on-a-desk-41359-large.mp4";

  const openVideoPopup = () => {
    setIsVideoPopupOpen(true);
  };

  const closeVideoPopup = () => {
    setIsVideoPopupOpen(false);
  };

  const handleAccessDocument = () => {
    // Navigate to payment page
    navigate('/payment');
  };

  
const quickStats = [
  { icon: "📄", value: "24.5K", label: "Total Files", change: "+12%", trend: "up" },
  { icon: "👥", value: "1,247", label: "Active Users", change: "+8%", trend: "up" },
  { icon: "🔄", value: "3,892", label: "Workflows", change: "+15%", trend: "up" },
  { icon: "⚡", value: "98.9%", label: "Uptime", change: "+0.2%", trend: "up" }
];

const recentActivities = [
  { 
    title: "Marketing_Campaign_2024.pdf uploaded", 
    user: "Sarah Johnson", 
    time: "2 min ago", 
    icon: "📄",
    type: "upload"
  },
  { 
    title: "Q4_Financial_Report.xlsx approved", 
    user: "Mike Chen", 
    time: "15 min ago", 
    icon: "✓",
    type: "approval"
  },
  { 
    title: "Employee_Handbook updated to v3.2", 
    user: "Emily Roberts", 
    time: "1 hour ago", 
    icon: "🔄",
    type: "update"
  },
  { 
    title: "Legal_Contract_Draft.docx shared", 
    user: "David Kim", 
    time: "2 hours ago", 
    icon: "🔗",
    type: "share"
  }
];

const fileTypes = [
  { icon: "📄", name: "PDF", count: "12,456", percentage: "85%", size: "8.2 GB", color: "red" },
  { icon: "📊", name: "Excel", count: "5,678", percentage: "65%", size: "3.1 GB", color: "green" },
  { icon: "📝", name: "Word", count: "4,234", percentage: "55%", size: "2.8 GB", color: "blue" },
  { icon: "🖼️", name: "Images", count: "2,199", percentage: "40%", size: "1.5 GB", color: "purple" }
];

const enhancedFeatures = [
  {
    icon: "🔍",
    title: "Smart Search & Discovery",
    description: "Find any document instantly with AI-powered semantic search and intelligent recommendations",
    highlights: [
      "Natural language search",
      "Auto-tagging & categorization",
      "Content-based discovery"
    ]
  },
  {
    icon: "⚡",
    title: "Automated Workflows",
    description: "Streamline document processes with intelligent routing and approval automation",
    highlights: [
      "Smart approval chains",
      "Version control",
      "OCR processing"
    ]
  },
  {
    icon: "📊",
    title: "Usage Analytics",
    description: "Gain insights into document usage patterns and collaboration metrics",
    highlights: [
      "Access tracking",
      "Collaboration insights",
      "Storage optimization"
    ]
  },
  {
    icon: "🔒",
    title: "Security & Compliance",
    description: "Protect sensitive information with real-time monitoring and compliance tracking",
    highlights: [
      "Access controls",
      "Audit trails",
      "Compliance reporting"
    ]
  }
];

const capabilities = [
  { icon: "🎯", text: "Smart Classification" },
  { icon: "🔐", text: "Advanced Encryption" },
  { icon: "📱", text: "Mobile Access" },
  { icon: "🌐", text: "Cloud Sync" },
  { icon: "🤝", text: "Real-time Collaboration" },
  { icon: "📈", text: "Analytics Dashboard" },
  { icon: "🔔", text: "Smart Notifications" },
  { icon: "♻️", text: "Auto Archiving" }
];


  const features = [
    {
      icon: "📁",
      title: "Centralized Repository",
      description: "Store all documents in one secure, searchable cloud repository with intelligent categorization"
    },
    {
      icon: "🔍",
      title: "Advanced Search",
      description: "AI-powered search with OCR, metadata filtering, and content recognition across all documents"
    },
    {
      icon: "🔄",
      title: "Version Control",
      description: "Automatic version tracking, change history, and document restoration capabilities"
    },
    {
      icon: "👥",
      title: "Collaboration Tools",
      description: "Real-time co-editing, comments, annotations, and secure sharing with permission controls"
    },
    {
      icon: "📝",
      title: "Workflow Automation",
      description: "Automate document routing, approvals, signatures, and retention policies"
    },
    {
      icon: "🛡️",
      title: "Security & Compliance",
      description: "Enterprise-grade security, encryption, audit trails, and compliance management"
    }
  ];

const documentWorkflow = [
  {
    phase: "Capture",
    icon: "📥",
    time: "Instant",
    title: "Document Capture & Upload",
    steps: [
      "Multi-format document upload",
      "OCR and text extraction",
      "Automatic metadata tagging",
      "Smart file categorization"
    ]
  },
  {
    phase: "Process",
    icon: "⚙️",
    time: "2-5 minutes",
    title: "Intelligent Processing",
    steps: [
      "AI-powered classification",
      "Content analysis & indexing",
      "Automated workflow routing",
      "Version control setup"
    ]
  },
  {
    phase: "Collaborate",
    icon: "👥",
    time: "Real-time",
    title: "Secure Collaboration",
    steps: [
      "Real-time co-editing",
      "Comment & annotation tools",
      "Approval workflows",
      "Access control management"
    ]
  },
  {
    phase: "Archive",
    icon: "📚",
    time: "Automated",
    title: "Smart Archiving",
    steps: [
      "Automated retention policies",
      "Compliance monitoring",
      "Storage optimization",
      "Audit trail maintenance"
    ]
  }
];

const documentMetrics = [
  { name: "PDF Documents", progress: 45 },
  { name: "Word Files", progress: 28 },
  { name: "Excel Spreadsheets", progress: 15 },
  { name: "PowerPoint Files", progress: 8 }
];

const documentIntegrations = [
  "Google Drive", "Microsoft 365", "Dropbox", "OneDrive", 
  "Box", "Slack", "Salesforce", "SharePoint", 
  "Notion", "Confluence", "Jira", "Adobe Sign"
];



const openDocumentVideoPopup = () => {
  // Open document management video popup
  console.log("Open document management video");
};

  return (
    <div className="document-container">
      {/* Scroll Progress Bar */}
      {/* <div className="document-scroll-progress" style={{width: `${scrollProgress}%`}}></div> */}

      {/* Video Popup */}
      <VideoPopup 
        isOpen={isVideoPopupOpen} 
        onClose={closeVideoPopup} 
        videoUrl={demoVideoUrl}
      />

      {/* Enhanced Animated Background */}
      <div className="document-background-animation">
        {/* <div className="document-floating-shape document-shape-1"></div>
        <div className="document-floating-shape document-shape-2"></div>
        <div className="document-floating-shape document-shape-3"></div>
        <div className="document-floating-shape document-shape-4"></div>
        <div className="document-floating-shape document-shape-5"></div> */}
        
        {/* Animated Gradient Orbs */}
        {/* <div className="document-gradient-orb document-orb-1"></div>
        <div className="document-gradient-orb document-orb-2"></div>
        <div className="document-gradient-orb document-orb-3"></div> */}
        
        {/* SVG Background Animation */}
        <svg className="document-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="document-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(74, 144, 226, 0.08)" />
              <stop offset="50%" stopColor="rgba(120, 119, 216, 0.05)" />
              <stop offset="100%" stopColor="rgba(226, 74, 144, 0.08)" />
            </linearGradient>
            <linearGradient id="document-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(120, 119, 216, 0.06)" />
              <stop offset="50%" stopColor="rgba(119, 216, 216, 0.04)" />
              <stop offset="100%" stopColor="rgba(74, 226, 144, 0.06)" />
            </linearGradient>
            <pattern id="document-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#document-grid)" />
          <path 
            d="M0,15 Q30,5 60,25 T100,35 L100,100 L0,100 Z" 
            fill="url(#document-gradient1)"
            className="document-svg-path-1"
          />
          <path 
            d="M0,65 Q40,45 80,70 T100,85 L100,100 L0,100 Z" 
            fill="url(#document-gradient2)"
            opacity="0.8"
            className="document-svg-path-2"
          />
        </svg>
      </div>

<DocumentHero/>
<DocumentWiseCode/>
<HRCopilotFeatures/>
<HRCopilotNewsSection/>

    </div>
  );
};

export default DocumentManagement;